import { CircularProgress, Stack, Typography } from "@mui/material";
import {
  CheckInCircleIcon,
  HubSpotIcon,
  SalesForceIcon,
} from "../../../../images";
import { CustomButton } from "../../../../ui/CustomButton";
import React, { ReactNode, useEffect, useState } from "react";
import { SyncCRMModal } from "../../../../ui/modal/SyncCRMModal/SyncCRMModal";
import { useLazyGetCRMListQuery } from "../../../../../api";
import { CRMData, CRMListItem } from "../../../../../types/apiTypes";
import { toast } from "react-toastify";
import { CustomToast } from "../../../../ui/CustomToast";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";
import { useGetCRMUrlMutation } from "../../../../../api";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../../types/enums";

const Method = ({
  icon,
  title,
  text,
  btnHandler,
  isLoading,
  isSynced,
  isDisabled,
}: {
  icon: ReactNode;
  title: string;
  text: string;
  btnHandler: () => void;
  isLoading: boolean;
  isSynced: boolean;
  isDisabled: boolean;
}) => {
  return (
    <Stack
      sx={{
        gap: "16px",
        border: "1px solid #D9D9D9",
        borderRadius: "8px",
        padding: "16px",
        width: "100%",
      }}
    >
      <Stack gap="8px">
        <Stack flexDirection="row" gap="4px" alignItems="center">
          {icon}
          <Typography
            component="span"
            variant="button"
            color="custom.textDarkBlue"
          >
            {title}
          </Typography>
        </Stack>
        <Typography component="span" variant="caption" color="#4C545B">
          {text}
        </Typography>
      </Stack>
      {isSynced ? (
        <Stack flexDirection="row" gap="4px">
          <Typography component="span" variant="button" color="#4C545B">
            Synced
          </Typography>
          <CheckInCircleIcon />
        </Stack>
      ) : (
        <CustomButton
          onClick={btnHandler}
          variant="outlined"
          customColor="#FFFFFF"
          textColor="#1D3E70"
          type="button"
          customSize="fit"
          sx={{
            height: "32px",
            borderColor: "#1D3E70",
          }}
          disabled={isDisabled || isLoading}
        >
          Sync Data{" "}
          {isLoading && <CircularProgress size={20} color="inherit" />}
        </CustomButton>
      )}
    </Stack>
  );
};

export type UploadMethod = "hubspot" | "salesforce" | "";

export const CRMUploadMethod = ({
  setDataSource,
  isDisabled,
  currentCrm,
}: {
  setDataSource: (data: CRMData) => void;
  isDisabled: boolean;
  currentCrm: UploadMethod;
}) => {
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState<UploadMethod>("");
  const [crm, setCrm] = useState<UploadMethod>("");
  const [uploadMethod, setUploadMethod] = useState<UploadMethod>(currentCrm);
  const [crmList, setCrmList] = useState<CRMListItem[]>([]);

  const [getCRMUrl] = useGetCRMUrlMutation();
  const [getCRMListQuery, { isFetching }] = useLazyGetCRMListQuery();

  useEffect(() => {
    setUploadMethod(currentCrm);
  }, [currentCrm]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const crm = urlParams.get("crm");
    if (crm) {
      setCrm(crm as UploadMethod);
      navigate(AppRoutes.CREATE_CAMPAIGN, {
        replace: true,
      });
    }
  }, []);

  useEffect(() => {
    if (crm) {
      getCRMListQuery(crm)
        .unwrap()
        .then((data) => {
          setCrmList(data || []);
          setOpenModal(crm);
          setCrm("");
        })
        .catch(async (err) => {
          if (err?.status === 428) {
            getCRMUrl({
              campaign_id: "",
              action_type: "create",
              redirect_path: location.pathname,
              crm: crm,
            })
              .unwrap()
              .then((data) => {
                window.location.href = data.url;
              })
              .catch((err) =>
                toast.error(
                  <CustomToast
                    type="error"
                    text={
                      getErrorMessage(err).globalError ||
                      "Something went wrong. Please try again"
                    }
                    title="Connection Failed."
                  />,
                ),
              );
          } else {
            toast.error(
              <CustomToast
                type="error"
                text={
                  getErrorMessage(err).globalError ||
                  "The company data upload failed."
                }
                title="Upload Failed"
              />,
            );
          }
        });
    }
  }, [crm]);

  const setDataSourceHandler = (data: CRMData) => {
    setDataSource(data);
    setUploadMethod(data.crm);
  };

  return (
    <Stack flexDirection="row" gap="8px">
      <Method
        isLoading={crm === "hubspot"}
        icon={<HubSpotIcon />}
        title="HubSpot"
        btnHandler={() => setCrm("hubspot")}
        text="Automatically import all your contacts and accounts directly from HubSpot CRM into the platform."
        isSynced={uploadMethod === "hubspot"}
        isDisabled={!!uploadMethod || isDisabled}
      />
      <Method
        isLoading={crm === "salesforce"}
        icon={<SalesForceIcon />}
        title="Salesforce"
        btnHandler={() => setCrm("salesforce")}
        text="Quickly bring your Salesforce CRM contacts and accounts into the system to start working right away."
        isSynced={uploadMethod === "salesforce"}
        isDisabled={!!uploadMethod || isDisabled}
      />
      {openModal && (
        <SyncCRMModal
          crmList={crmList}
          openModal={openModal}
          onClose={() => setOpenModal("")}
          setDataSource={setDataSourceHandler}
          setOpenModal={setCrm}
          isLoading={isFetching}
        />
      )}
    </Stack>
  );
};
